import { useEffect, useMemo, useState } from "react";
import {
  X,
  Save,
  Loader,
  User as UserIcon,
  Mail,
  Shield,
  KeyRound,
  Activity,
  Info,
  Crown,
  Clock,
} from "lucide-react";
import styles from "./UserDetail.module.css";
import PermissionMatrix from "../../../components/UsersPermissions/PermissionMatrix";
import { useToast } from "../../../context/ToastContext";
import {
  useUserDetail,
  useUpdateUser,
  useAssignUserRoles,
  useAssignUserPermissions,
  useRemoveUserPermissions,
} from "../../../api/users/useUsersApi";
import { useRolesList } from "../../../api/users/useRolesApi";
import { usePermissionsList } from "../../../api/users/usePermissionsApi";
import { useAuditLogs } from "../../../api/users/useAuditApi";

const STATUSES = [
  { value: "ACTIVE", label: "Active" },
  { value: "SUSPENDED", label: "Suspended" },
  { value: "DEACTIVATED", label: "Deactivated" },
];

const prettyRole = (name = "") =>
  name
    .split("_")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");

const fmtDateTime = (d) =>
  d
    ? new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false })
    : "—";

const UserDetail = ({ userId, onClose, onSaved }) => {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState("overview");

  const { data: user, loading: userLoading, refetch } = useUserDetail(userId);
  const { data: roles } = useRolesList();
  const { data: permissions } = usePermissionsList();
  const { data: activity, loading: activityLoading } = useAuditLogs({
    userId,
    limit: 25,
  });

  const { execute: updateUser, loading: savingProfile } = useUpdateUser();
  const { execute: assignRoles, loading: savingRole } = useAssignUserRoles();
  const { execute: assignPerms, loading: addingPerms } = useAssignUserPermissions();
  const { execute: removePerms, loading: removingPerms } = useRemoveUserPermissions();

  const saving =
    savingProfile || savingRole || addingPerms || removingPerms;

  const [form, setForm] = useState({ name: "", email: "", status: "ACTIVE" });
  const [roleId, setRoleId] = useState("");
  const [directIds, setDirectIds] = useState([]);
  const [original, setOriginal] = useState(null);

  // hydrate state when the user loads
  useEffect(() => {
    if (!user) return;
    const rId = user.Role?.id ?? user.currentRoleId ?? "";
    const direct = (user.Permissions || []).map((p) => Number(p.id));
    setForm({
      name: user.name || "",
      email: user.email || "",
      status: user.status || "ACTIVE",
    });
    setRoleId(rId ? Number(rId) : "");
    setDirectIds(direct);
    setOriginal({
      name: user.name || "",
      email: user.email || "",
      status: user.status || "ACTIVE",
      roleId: rId ? Number(rId) : "",
      directIds: direct,
    });
  }, [user]);

  const role = useMemo(
    () => (Array.isArray(roles) ? roles.find((r) => Number(r.id) === Number(roleId)) : null),
    [roles, roleId]
  );

  // permissions inherited from the selected role (locked in the matrix)
  const inheritedIds = useMemo(
    () => (role?.Permissions || []).map((p) => Number(p.id)),
    [role]
  );

  // direct ids that are NOT already covered by the role
  const effectiveDirect = useMemo(
    () => directIds.filter((id) => !inheritedIds.includes(Number(id))),
    [directIds, inheritedIds]
  );

  const dirty = useMemo(() => {
    if (!original) return false;
    if (
      form.name !== original.name ||
      form.email !== original.email ||
      form.status !== original.status ||
      Number(roleId) !== Number(original.roleId)
    )
      return true;
    const a = [...effectiveDirect].sort().join(",");
    const b = [...original.directIds].filter((id) => !inheritedIds.includes(Number(id))).sort().join(",");
    return a !== b;
  }, [form, roleId, effectiveDirect, original, inheritedIds]);

  const handleSave = async () => {
    if (!user) return;
    try {
      // 1. profile fields
      if (
        form.name !== original.name ||
        form.email !== original.email ||
        form.status !== original.status
      ) {
        await updateUser(user.id, {
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          status: form.status,
        });
      }

      // 2. role change (replaces roles)
      if (Number(roleId) !== Number(original.roleId) && roleId) {
        await assignRoles(user.id, [Number(roleId)]);
      }

      // 3. direct permission diff (excluding role-inherited)
      const before = original.directIds.filter((id) => !inheritedIds.includes(Number(id)));
      const after = effectiveDirect;
      const toAdd = after.filter((id) => !before.includes(id));
      const toRemove = before.filter((id) => !after.includes(id));
      if (toAdd.length) await assignPerms(user.id, toAdd);
      if (toRemove.length) await removePerms(user.id, toRemove);

      showToast("User updated successfully", "success");
      await refetch();
      onSaved?.();
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to update user", "error");
    }
  };

  const tabs = [
    { id: "overview", label: "Profile & Role", icon: UserIcon },
    { id: "permissions", label: "Direct Permissions", icon: KeyRound },
    { id: "activity", label: "Activity", icon: Activity },
  ];

  const isSuper = (role?.name || "").toLowerCase().includes("super");

  return (
    <>
      <div className={styles.backdrop} onClick={onClose} />
      <div className={styles.drawer}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <img
              src={`https://i.pravatar.cc/80?u=${user?.email || userId}`}
              alt={user?.name}
              className={styles.avatar}
            />
            <div>
              <h2 className={styles.name}>
                {user?.name || "Loading…"}
                {isSuper && <Crown size={15} className={styles.crown} />}
              </h2>
              <div className={styles.headerMeta}>
                <span className={styles.metaEmail}>{user?.email}</span>
                {role && (
                  <span className={styles.roleChip}>{prettyRole(role.name)}</span>
                )}
              </div>
            </div>
          </div>
          <div className={styles.headerActions}>
            <button className={styles.closeBtn} onClick={onClose} disabled={saving}>
              <X size={18} /> Close
            </button>
            <button
              className={styles.saveBtn}
              onClick={handleSave}
              disabled={saving || !dirty}
            >
              {saving ? (
                <>
                  <Loader size={16} className={styles.spin} /> Saving…
                </>
              ) : (
                <>
                  <Save size={16} /> Save Changes
                </>
              )}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className={styles.tabs}>
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                className={`${styles.tab} ${activeTab === t.id ? styles.tabActive : ""}`}
                onClick={() => setActiveTab(t.id)}
              >
                <Icon size={16} />
                {t.label}
              </button>
            );
          })}
        </div>

        <div className={styles.content}>
          {userLoading && !user ? (
            <div className={styles.loading}>Loading user…</div>
          ) : (
            <>
              {/* ── Overview ── */}
              {activeTab === "overview" && (
                <div className={styles.tabPane}>
                  <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Profile</h3>
                    <div className={styles.grid}>
                      <div className={styles.field}>
                        <label className={styles.label}>Full Name</label>
                        <div className={styles.inputWrap}>
                          <UserIcon size={15} className={styles.inputIcon} />
                          <input
                            className={styles.input}
                            value={form.name}
                            onChange={(e) =>
                              setForm((f) => ({ ...f, name: e.target.value }))
                            }
                          />
                        </div>
                      </div>
                      <div className={styles.field}>
                        <label className={styles.label}>Email</label>
                        <div className={styles.inputWrap}>
                          <Mail size={15} className={styles.inputIcon} />
                          <input
                            className={styles.input}
                            value={form.email}
                            onChange={(e) =>
                              setForm((f) => ({ ...f, email: e.target.value }))
                            }
                          />
                        </div>
                      </div>
                    </div>

                    <div className={styles.field}>
                      <label className={styles.label}>Status</label>
                      <div className={styles.segmented}>
                        {STATUSES.map((s) => (
                          <button
                            key={s.value}
                            className={`${styles.segBtn} ${
                              form.status === s.value ? styles.segActive : ""
                            } ${styles[`seg_${s.value}`]}`}
                            onClick={() => setForm((f) => ({ ...f, status: s.value }))}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>
                      <Shield size={16} /> Role Assignment
                    </h3>
                    <p className={styles.sectionHint}>
                      A user inherits all permissions from their role. Choose one
                      primary role.
                    </p>
                    <div className={styles.roleGrid}>
                      {(Array.isArray(roles) ? roles : [])
                        .filter((r) => r.status === "ACTIVE")
                        .map((r) => {
                          const active = Number(roleId) === Number(r.id);
                          return (
                            <button
                              key={r.id}
                              className={`${styles.roleCard} ${
                                active ? styles.roleCardActive : ""
                              }`}
                              onClick={() => setRoleId(Number(r.id))}
                            >
                              <div className={styles.roleCardHead}>
                                <Shield size={15} />
                                <span className={styles.roleCardName}>
                                  {prettyRole(r.name)}
                                </span>
                                {active && <span className={styles.roleDot} />}
                              </div>
                              <span className={styles.roleCardDesc}>
                                {r.description || "—"}
                              </span>
                              <span className={styles.roleCardCount}>
                                {(r.Permissions?.length ?? 0)} permissions
                              </span>
                            </button>
                          );
                        })}
                    </div>
                  </div>

                  <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>
                      <Clock size={16} /> Account Info
                    </h3>
                    <div className={styles.auditGrid}>
                      <div className={styles.auditCard}>
                        <span className={styles.auditLabel}>User ID</span>
                        <span className={styles.auditValue}>#{user?.id}</span>
                      </div>
                      <div className={styles.auditCard}>
                        <span className={styles.auditLabel}>Created</span>
                        <span className={styles.auditValue}>
                          {fmtDateTime(user?.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Permissions ── */}
              {activeTab === "permissions" && (
                <div className={styles.tabPane}>
                  <div className={styles.infoBox}>
                    <Info size={16} />
                    <span>
                      <b>{inheritedIds.length}</b> permissions are inherited from the{" "}
                      <b>{role ? prettyRole(role.name) : "selected"}</b> role (shown
                      locked). Toggle additional <b>direct</b> permissions below.
                    </span>
                  </div>
                  <PermissionMatrix
                    permissions={permissions}
                    value={effectiveDirect}
                    inheritedIds={inheritedIds}
                    onChange={(ids) => setDirectIds(ids)}
                  />
                </div>
              )}

              {/* ── Activity ── */}
              {activeTab === "activity" && (
                <div className={styles.tabPane}>
                  {activityLoading ? (
                    <div className={styles.loading}>Loading activity…</div>
                  ) : activity.length === 0 ? (
                    <div className={styles.emptyActivity}>
                      <Activity size={26} />
                      <p>No recorded activity for this user yet.</p>
                    </div>
                  ) : (
                    <div className={styles.timeline}>
                      {activity.map((log) => (
                        <div key={log.id} className={styles.timelineItem}>
                          <div className={styles.timelineDot} />
                          <div className={styles.timelineBody}>
                            <div className={styles.timelineTop}>
                              <span className={styles.timelineAction}>
                                {log.action}
                              </span>
                              <span className={styles.timelineDate}>
                                {fmtDateTime(log.createdAt)}
                              </span>
                            </div>
                            <div className={styles.timelineMeta}>
                              {log.entity}
                              {log.entityId ? ` · #${log.entityId}` : ""}
                              {log.reason ? ` · ${log.reason}` : ""}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default UserDetail;
